<script setup lang="ts" generic="T extends { id: string; name: string }">
import { ref, computed, watch } from 'vue'
import UserObjectItem from '@/features/user/edit/ui/UserObjectItem.vue'
import { ButtonElement, PaginationBase, SaveButton } from '.'
import { Plus, Trash } from '@lucide/vue'

interface IPagination {
  total: number
  pageSize: number
  pageNumber: number
  pageTotal: number
}

const props = withDefaults(
  defineProps<{
    modelValue: T[]
    selectedTitle: string
    availableTitle: string
    availableItems: T[]
    availablePagination?: IPagination
    isSaving?: boolean
    pageSize?: number
    showSelectedPagination?: boolean
  }>(),
  { pageSize: 10 },
)

const emit = defineEmits<{
  'update:modelValue': [items: T[]]
  save: [ids: string[]]
  availablePaginate: [pageSize: number, pageNumber: number]
}>()

const selectedFilter = ref({ pageNumber: 1, pageSize: props.pageSize })

const selectedPaging = computed<IPagination>(() => {
  const total = props.modelValue.length
  const size = selectedFilter.value.pageSize
  const pageTotal = Math.max(Math.ceil(total / size), 1)
  return {
    total,
    pageSize: size,
    pageNumber: selectedFilter.value.pageNumber,
    pageTotal,
  }
})

watch(
  () => props.modelValue.length,
  () => {
    const size = selectedFilter.value.pageSize
    const pageTotal = Math.max(Math.ceil(props.modelValue.length / size), 1)
    if (selectedFilter.value.pageNumber > pageTotal) {
      selectedFilter.value.pageNumber = pageTotal
    }
  },
)

const paginatedSelectedData = computed(() => {
  const start = (selectedFilter.value.pageNumber - 1) * selectedFilter.value.pageSize
  return props.modelValue.slice(start, start + selectedFilter.value.pageSize)
})

const add = (id: string, name: string) => {
  if (props.modelValue.some((el) => el.id === id)) return
  emit('update:modelValue', [...props.modelValue, { id, name } as T])
}

const remove = (id: string) => {
  emit(
    'update:modelValue',
    props.modelValue.filter((el) => el.id !== id),
  )
}

const removeAll = () => emit('update:modelValue', [])

const addAll = () => {
  const existingIds = new Set(props.modelValue.map((el) => el.id))
  const merged = [
    ...props.modelValue,
    ...props.availableItems.filter((el) => !existingIds.has(el.id)),
  ]
  emit('update:modelValue', merged)
}
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="flex min-h-0 flex-1 gap-10 justify-between">
      <div class="flex min-h-0 flex-1 flex-col">
        <div class="flex items-center justify-between mb-4">
          <div class="flex gap-4 justify-between w-full pr-4">
            <p class="font-bold text-gray-800 dark:text-white/90">{{ selectedTitle }}</p>

            <div class="flex gap-2 items-end">
              <PaginationBase
                v-if="showSelectedPagination && modelValue.length > selectedFilter.pageSize"
                :pagination="selectedPaging"
                @paginig="
                  (s, n) => {
                    selectedFilter.pageSize = s
                    selectedFilter.pageNumber = n
                  }
                "
              />
              <ButtonElement v-if="modelValue.length" @click="removeAll" variant="compact">
                <Trash :size="12" />
              </ButtonElement>
              <ButtonElement @click="addAll" variant="compact"><Plus :size="12" /></ButtonElement>
            </div>
          </div>
        </div>
        <div class="w-full overflow-y-auto pr-4">
          <UserObjectItem
            v-for="item in paginatedSelectedData"
            :key="item.id"
            :id="item.id"
            :name="item.name"
            iconType="remove"
            @link-pressed="remove"
          />
        </div>
      </div>

      <div class="flex min-h-0 flex-1 flex-col">
        <div class="flex items-center justify-between mb-4">
          <div class="flex gap-4 justify-between w-full pr-4">
            <p class="font-bold text-gray-800 dark:text-white/90">{{ availableTitle }}</p>
            <div class="flex gap-2 items-end">
              <PaginationBase
                v-if="availablePagination"
                :pagination="availablePagination"
                @paginig="(s, n) => emit('availablePaginate', s, n)"
              />
            </div>
          </div>
        </div>

        <div class="w-full overflow-y-auto pr-4">
          <UserObjectItem
            v-for="item in availableItems"
            :key="item.id"
            :id="item.id"
            :name="item.name"
            @link-pressed="add"
          />
        </div>
      </div>
    </div>
    <div class="flex justify-end gap-5 shrink-0 pt-4">
      <SaveButton
        @click="
          emit(
            'save',
            modelValue.map((el) => el.id),
          )
        "
        :disabled="isSaving"
      />
    </div>
  </div>
</template>
